import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import MethodPill from '@/components/ui/common/method-pill';
import ParameterComparisonTable from '@/components/ui/diagram/parameter-comparison-table';
import { BodyFieldComparison } from '@/components/ui/diagram/body-field-tree';
import * as overviewService from '@/routes/overview/service/overview';
import { ArrowRight, ChevronDown, ChevronRight, X } from 'lucide-react';
import './diagram.css';

const EndpointCell = ({ endpoint, dim }) => (
	<div className={`flex items-center gap-1.5 min-w-0 ${dim ? 'opacity-40' : ''}`}>
		<MethodPill method={endpoint.method} />
		<span className="font-mono text-xs truncate">{endpoint.path}</span>
	</div>
);

const EmptyCell = () => <div className="text-gray-600 text-xs italic">&mdash;</div>;

const StatusDot = ({ status }) => {
	const colors = { matched: 'bg-[#96EF48]', mismatch: 'bg-red-500', unused: 'bg-gray-600' };
	return <span className={`w-1.5 h-1.5 rounded-full shrink-0 ${colors[status]}`} />;
};

const DetailSection = ({ title, children }) => (
	<div className="border-t border-border/10">
		<div className="px-2 py-1 bg-navbarBackground text-[10px] text-muted-foreground uppercase tracking-wider font-semibold grid grid-cols-2 gap-px">
			<span>Consumer {title}</span>
			<span>Producer {title}</span>
		</div>
		{children}
	</div>
);

const ComparisonRow = ({ left, right, status, errors = [], consumerEndpoint, producerEndpoint }) => {
	const [expanded, setExpanded] = useState(false);
	const hasErrors = errors.length > 0;
	const isMatched = status === 'matched';
	const isExpandable = isMatched && (left && right);
	const rowStatus = hasErrors ? 'error' : status;

	const consumerReq = consumerEndpoint?.request;
	const producerReq = producerEndpoint?.request;
	const consumerRes = consumerEndpoint?.response;
	const producerRes = producerEndpoint?.response;

	const hasParams = consumerReq?.parameters?.length > 0 || producerReq?.parameters?.length > 0;
	const hasReqBody = consumerReq?.bodyFields?.length > 0 || producerReq?.bodyFields?.length > 0;
	const hasResBody = consumerRes?.bodyFields?.length > 0 || producerRes?.bodyFields?.length > 0;
	const hasDetail = hasParams || hasReqBody || hasResBody;

	const paramErrors = errors.filter((e) => e.section === 'request.parameters');
	const reqBodyErrors = errors.filter((e) => e.section === 'request.body');
	const resBodyErrors = errors.filter((e) => e.section === 'response.body');
	const unstructuredErrors = errors.filter((e) => !e.errorType);

	return (
		<div className="border-b border-border/10 last:border-b-0">
			<div
				className={`grid grid-cols-[1fr_24px_1fr] items-center gap-1 px-3 py-1.5 hover:bg-muted/20 ${isExpandable || hasErrors ? 'cursor-pointer' : ''}`}
				onClick={isExpandable || hasErrors ? () => setExpanded(!expanded) : undefined}
			>
				<div className="flex items-center gap-1.5 min-w-0">
					{isExpandable || hasErrors ? (
						expanded ? (
							<ChevronDown size={12} className={`${hasErrors ? 'text-red-400' : 'text-gray-500'} shrink-0`} />
						) : (
							<ChevronRight size={12} className={`${hasErrors ? 'text-red-400' : 'text-gray-500'} shrink-0`} />
						)
					) : (
						<StatusDot status={rowStatus} />
					)}
					{left ? <EndpointCell endpoint={left} /> : <EmptyCell />}
				</div>
				<ArrowRight size={12} className="text-gray-600 mx-auto shrink-0" />
				<div className="flex items-center gap-1.5 min-w-0">
					{right ? <EndpointCell endpoint={right} dim={status === 'unused'} /> : <EmptyCell />}
					{hasErrors && (
						<span className="text-[10px] text-red-400 font-mono shrink-0 ml-auto">
							{errors.length} error{errors.length !== 1 ? 's' : ''}
						</span>
					)}
				</div>
			</div>
			{expanded && (
				<div className="bg-muted/10 border-t border-border/10">
					{hasDetail ? (
						<>
							{hasParams && (
								<DetailSection title="Parameters">
									<ParameterComparisonTable
										consumerParams={consumerReq?.parameters || []}
										producerParams={producerReq?.parameters || []}
										errors={paramErrors}
									/>
								</DetailSection>
							)}
							{hasReqBody && (
								<DetailSection title="Request Body">
									<BodyFieldComparison
										consumerFields={consumerReq?.bodyFields || []}
										producerFields={producerReq?.bodyFields || []}
										errors={reqBodyErrors}
										section="request.body"
									/>
								</DetailSection>
							)}
							{hasResBody && (
								<DetailSection title="Response Body">
									<BodyFieldComparison
										consumerFields={consumerRes?.bodyFields || []}
										producerFields={producerRes?.bodyFields || []}
										errors={resBodyErrors}
										section="response.body"
									/>
								</DetailSection>
							)}
							{!hasParams && !hasReqBody && !hasResBody && (
								<div className="text-xs text-muted-foreground italic px-3 py-2">No schema details available</div>
							)}
						</>
					) : (
						<div className="text-xs text-muted-foreground italic px-3 py-2">No schema details available</div>
					)}

					{/* Fallback: unstructured errors without metadata */}
					{unstructuredErrors.length > 0 && (
						<div className="border-t border-border/10 px-3 py-1">
							{unstructuredErrors.map((err, i) => (
								<div key={i} className="flex items-center gap-1.5 py-1 text-xs">
									<span className="w-1 h-1 rounded-full bg-red-500 shrink-0" />
									<span className="text-red-400 break-all">{err.message}</span>
								</div>
							))}
						</div>
					)}
				</div>
			)}
		</div>
	);
};

const EndpointComparisonTab = ({ edge }) => {
	const [comparison, setComparison] = useState(null);
	const [loading, setLoading] = useState(false);
	const [errorsOnly, setErrorsOnly] = useState(false);

	useEffect(() => {
		let cancelled = false;
		const load = async () => {
			setLoading(true);
			setComparison(null);
			try {
				const data = await overviewService.getEdgeContract(edge.source, edge.target);
				if (!cancelled) setComparison(data);
			} catch (error) {
				console.error('Failed to fetch edge contract:', error);
			} finally {
				if (!cancelled) setLoading(false);
			}
		};
		load();
		return () => {
			cancelled = true;
		};
	}, [edge.source, edge.target]);

	if (loading) {
		return (
			<div className="p-3 space-y-2">
				<Skeleton className="h-6 w-full" />
				<Skeleton className="h-6 w-3/4" />
				<Skeleton className="h-6 w-1/2" />
			</div>
		);
	}

	if (!comparison) {
		return <div className="text-gray-500 p-3">No endpoint data available for this connection.</div>;
	}

	const { matched, consumerOnly, producerOnly, consumerTargetUrl, producerBaseUrl } = comparison;
	const isEmpty = matched.length === 0 && consumerOnly.length === 0 && producerOnly.length === 0;
	const hasAnyErrors = matched.some((m) => m.errors?.length > 0) || consumerOnly.length > 0;

	if (isEmpty) {
		return <div className="text-gray-500 p-3">No endpoints found for either service.</div>;
	}

	const filteredMatched = errorsOnly ? matched.filter((m) => m.errors?.length > 0) : matched;
	const filteredConsumerOnly = errorsOnly ? consumerOnly : consumerOnly;
	const filteredProducerOnly = errorsOnly ? [] : producerOnly;

	return (
		<div className="overflow-y-auto h-full">
			{/* Column headers */}
			<div className="grid grid-cols-[1fr_24px_1fr] gap-1 px-3 py-1.5 border-b border-border/30 bg-background sticky top-0 z-10">
				<div>
					<div className="text-xs font-semibold text-[#EFD948] uppercase tracking-wide">
						{edge.source} <span className="text-gray-500 normal-case font-normal">(consumer)</span>
					</div>
					{consumerTargetUrl && (
						<div className="text-[10px] font-mono text-gray-500 truncate mt-0.5">calls {consumerTargetUrl}</div>
					)}
				</div>
				<div className="flex items-center">
					<ArrowRight size={12} className="text-gray-600 mx-auto" />
				</div>
				<div className="flex flex-col">
					<div className="flex items-center justify-between">
						<span className="text-xs font-semibold text-[#96EF48] uppercase tracking-wide">
							{edge.target} <span className="text-gray-500 normal-case font-normal">(producer)</span>
						</span>
						{hasAnyErrors && (
							<button
								onClick={() => setErrorsOnly(!errorsOnly)}
								className={`text-[10px] px-1.5 py-0.5 rounded font-mono transition-colors ${
									errorsOnly
										? 'bg-red-500/20 text-red-400'
										: 'bg-muted/30 text-gray-500 hover:text-gray-400'
								}`}
							>
								errors only
							</button>
						)}
					</div>
					{producerBaseUrl && (
						<div className="text-[10px] font-mono text-gray-500 truncate mt-0.5">exposed at {producerBaseUrl}</div>
					)}
				</div>
			</div>

			{/* Matched rows */}
			{filteredMatched.map((m, i) => (
				<ComparisonRow
					key={`m-${i}`}
					left={m.consumer}
					right={m.producer}
					status="matched"
					errors={m.errors}
					consumerEndpoint={m.consumer}
					producerEndpoint={m.producer}
				/>
			))}

			{/* Consumer-only (mismatches) */}
			{filteredConsumerOnly.map((ep, i) => (
				<ComparisonRow key={`c-${i}`} left={ep} right={null} status="mismatch" />
			))}

			{/* Producer-only (unused) */}
			{filteredProducerOnly.map((ep, i) => (
				<ComparisonRow key={`p-${i}`} left={null} right={ep} status="unused" />
			))}

			{errorsOnly && filteredMatched.length === 0 && filteredConsumerOnly.length === 0 && (
				<div className="text-gray-500 text-sm p-3 text-center">No errors in this connection.</div>
			)}
		</div>
	);
};

const ErrorLogContent = ({ edge }) => {
	const { source, target } = edge;
	if (edge.data.contractMatchingErrors.length > 0) {
		return (
			<ul className="list-disc pl-5 mt-2 max-h-[25vh] overflow-y-auto">
				{edge.data.contractMatchingErrors.map((error, index) => (
					<li key={index} className="text-white-500 flex items-center">
						<span className="break-all uppercase text-[#EFD948]">{source} (C)</span>
						<span className="inline-block mx-1">&rarr;</span>
						<span className="break-all uppercase text-[#96EF48]">{target} (P)</span>
						<span className="inline-block mx-1">&rarr;</span>
						<span className="break-all text-primary">{error.method + ' ' + error.path}</span>
						<span className="inline-block mx-1">&rarr;</span>
						<span className="break-all text-red-500">{error.message}</span>
					</li>
				))}
			</ul>
		);
	}
	return <div className="text-gray-500 mt-2">No contract errors found.</div>;
};

const DEFAULT_HEIGHT = 40;
const MIN_HEIGHT = 15;
const MAX_HEIGHT = 80;

const InteractionPanel = ({ edge, onClose = () => {} }) => {
	const panelRef = useRef(null);
	const [panelHeight, setPanelHeight] = useState(DEFAULT_HEIGHT);
	const isDragging = useRef(false);
	const startY = useRef(0);
	const startHeight = useRef(DEFAULT_HEIGHT);

	const handleMouseMove = useCallback((e) => {
		if (!isDragging.current) return;
		const deltaVh = ((startY.current - e.clientY) / window.innerHeight) * 100;
		setPanelHeight(Math.min(MAX_HEIGHT, Math.max(MIN_HEIGHT, startHeight.current + deltaVh)));
	}, []);

	const handleMouseUp = useCallback(() => {
		isDragging.current = false;
		document.removeEventListener('mousemove', handleMouseMove);
		document.removeEventListener('mouseup', handleMouseUp);
		document.body.style.userSelect = '';
	}, [handleMouseMove]);

	const handleDragStart = useCallback(
		(e) => {
			e.preventDefault();
			e.stopPropagation();
			isDragging.current = true;
			startY.current = e.clientY;
			startHeight.current = panelHeight;
			document.body.style.userSelect = 'none';
			document.addEventListener('mousemove', handleMouseMove);
			document.addEventListener('mouseup', handleMouseUp);
		},
		[panelHeight, handleMouseMove, handleMouseUp]
	);

	const handleClickOutside = (event) => {
		if (panelRef.current && !panelRef.current.contains(event.target)) {
			onClose();
		}
	};

	useEffect(() => {
		if (edge) {
			document.addEventListener('mousedown', handleClickOutside);
		} else {
			document.removeEventListener('mousedown', handleClickOutside);
		}
		return () => {
			document.removeEventListener('mousedown', handleClickOutside);
		};
	}, [edge]);

	useEffect(() => {
		return () => {
			document.removeEventListener('mousemove', handleMouseMove);
			document.removeEventListener('mouseup', handleMouseUp);
			document.body.style.userSelect = '';
		};
	}, [handleMouseMove, handleMouseUp]);

	const errorCount = edge.data.contractMatchingErrors?.length || 0;

	return (
		<div
			className="border rounded-t-lg rounded-b-lg absolute bottom-0 left-0 right-0 bg-background border-t border-border z-10 flex flex-col"
			style={{ height: `${panelHeight}vh` }}
			ref={panelRef}
		>
			<div
				className="group flex items-center justify-center h-1.5 cursor-ns-resize shrink-0 hover:bg-muted/30 transition-colors"
				onMouseDown={handleDragStart}
				onDoubleClick={() => setPanelHeight(DEFAULT_HEIGHT)}
			>
				<div className="w-8 h-0.5 rounded-full bg-border/50 group-hover:bg-border transition-colors" />
			</div>
			<div className="p-3 border-b border-border/30 flex items-center justify-between shrink-0">
				<div className="flex items-center gap-2">
					<span className="text-sm font-semibold text-foreground">Contract Comparison</span>
					{errorCount > 0 && <span className="text-xs text-red-400">({errorCount} errors)</span>}
				</div>
				<Button className="text-secondary" variant="borderless" size="icon" onClick={onClose}>
					<X size={20} />
				</Button>
			</div>
			<div className="flex-1 overflow-hidden">
				<EndpointComparisonTab edge={edge} />
			</div>
		</div>
	);
};

export default InteractionPanel;
